import { useCallback, useMemo, useRef, useState } from "react";
import { deleteMailMessage, moveMailMessage } from "@/lib/api/mailActions";
import { relocateStoredMessages } from "@/lib/storage/mailStorageRelocate";
import { relocateMessageBodyCache } from "@/lib/cache/messageBodyCache";
import { markMailMessageSeen, markMailMessageUnread } from "@/lib/api/imap";
import { patchFolderSnapshotSeen } from "@/lib/mail/inboxFolderBus";
import { buildMessageContextMenuItems } from "@/lib/mail/mailMessageActions";
import type { MailColorLabel } from "@/lib/mail/messageLabels";
import type { useMailSnooze } from "@/hooks/useMailSnooze";
import type { useMessageLabels } from "@/hooks/useMessageLabels";
import type { MailAccountSummary, MailMessageSummary } from "@/lib/types/mail";
import { isGmailMailAccount } from "@/lib/accounts/googleOAuthAccounts";
import {
  holdMutationUids,
  releaseMutationUids,
  releaseMutationHoldsOnly,
  tombstoneUids,
} from "@/lib/mail/mailMutationHold";
import { enqueueMailMutation } from "@/lib/mail/mailMutationSerial";

type InboxMutations = {
  removeMessages: (uids: number[]) => void;
  markMessageSeen: (uid: number, options?: { force?: boolean }) => void;
  markMessageUnread: (uid: number, options?: { force?: boolean }) => void;
  setMessagesSeen: (uids: number[], seen: boolean, options?: { force?: boolean }) => void;
  markMessageFlagged: (uid: number, flagged: boolean) => void;
};

type ComposeApi = {
  openComposeForUid: (uid: number, mode: "reply" | "replyAll" | "forward") => Promise<void>;
  openDraftForEdit: (uid: number) => void;
};

type RefreshApi = {
  invalidateFolders: (spec: {
    accountId: number;
    folders: string[];
    reloadListIfViewing?: boolean;
    forceList?: boolean;
    preserveSnapshots?: string[];
    prefetchFolders?: string[];
    prefetchRefresh?: boolean;
    mutationJobId?: string;
  }) => void;
  recoverAfterMutationError: () => void;
};

type Options = {
  account: MailAccountSummary | null;
  accountId: number | null;
  folder: string;
  isOutboxView: boolean;
  isSnoozedView: boolean;
  isDraftsFolder: boolean;
  inboxListActive: boolean;
  displayMessages: MailMessageSummary[];
  labeledMessages: MailMessageSummary[];
  inbox: InboxMutations;
  messageLabels: ReturnType<typeof useMessageLabels>;
  mailSnooze: ReturnType<typeof useMailSnooze>;
  compose: ComposeApi;
  refresh: RefreshApi;
  adjustFolderUnread: (folder: string, delta: number) => void;
  resolveSnoozeHoldFolder?: () => string | null;
  resolveTrashFolder?: (accountId: number) => string | null;
  onSnoozeFolderRequired?: () => void;
  onRecover?: () => void;
  onCloseReader?: () => void;
  onArchiveToCoffre?: (message: MailMessageSummary) => void;
};

export function useMobileActions({
  account,
  accountId,
  folder,
  isOutboxView,
  isSnoozedView,
  isDraftsFolder,
  inboxListActive,
  displayMessages,
  labeledMessages,
  inbox,
  messageLabels,
  mailSnooze,
  compose,
  refresh,
  adjustFolderUnread,
  resolveSnoozeHoldFolder,
  resolveTrashFolder,
  onSnoozeFolderRequired,
  onRecover,
  onCloseReader,
  onArchiveToCoffre,
}: Options) {
  const [actionMessage, setActionMessage] = useState<MailMessageSummary | null>(null);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [snoozeDialog, setSnoozeDialog] = useState<{ uid: number; subject?: string } | null>(null);
  const [snoozeBusy, setSnoozeBusy] = useState(false);
  const snoozeBusyRef = useRef(false);
  const pendingMoveUidsRef = useRef<number[]>([]);

  const closeActions = useCallback(() => setActionMessage(null), []);

  const openActions = useCallback((message: MailMessageSummary) => {
    setActionMessage(message);
  }, []);

  const findMessageByUid = useCallback(
    (uid: number) =>
      displayMessages.find((m) => m.uid === uid) ??
      labeledMessages.find((m) => m.uid === uid),
    [displayMessages, labeledMessages]
  );

  const messageImapFolder = useCallback(
    (uid: number) => findMessageByUid(uid)?.imapFolder ?? folder,
    [findMessageByUid, folder]
  );

  const applySeen = useCallback(
    async (uid: number, seen: boolean) => {
      if (accountId == null || uid <= 0) return;
      const msg = findMessageByUid(uid);
      const fold = messageImapFolder(uid);
      const actionUid =
        msg?.imapBodyUid != null && msg.imapBodyUid > 0 ? msg.imapBodyUid : uid;

      if (seen) {
        if (msg && !msg.seen) adjustFolderUnread(fold, -1);
      } else if (msg?.seen) {
        adjustFolderUnread(fold, 1);
      }

      if (isSnoozedView) {
        // Hors liste inbox : patch snapshot (liste En attente) + API avec UID IMAP réel.
        patchFolderSnapshotSeen(accountId, fold, [actionUid], seen);
        const res = seen
          ? await markMailMessageSeen({ accountId, folder: fold, uids: [actionUid] })
          : await markMailMessageUnread({ accountId, folder: fold, uids: [actionUid] });
        if (!res.ok) {
          patchFolderSnapshotSeen(accountId, fold, [actionUid], !seen);
          onRecover?.();
        }
        return;
      }

      if (seen) {
        inbox.markMessageSeen(uid, { force: true });
      } else {
        inbox.markMessageUnread(uid, { force: true });
      }
    },
    [
      accountId,
      adjustFolderUnread,
      findMessageByUid,
      inbox,
      isSnoozedView,
      messageImapFolder,
      onRecover,
    ]
  );

  const deleteMessage = useCallback(
    async (uid: number, seenBefore?: boolean) => {
      if (accountId == null || uid <= 0 || isOutboxView) return;

      const fold = messageImapFolder(uid);
      if (mailSnooze.isSnoozed(fold, uid)) {
        void mailSnooze.unsnooze(fold, uid);
      }

      tombstoneUids(
        accountId,
        fold,
        [uid],
        undefined,
        seenBefore === false ? new Set([uid]) : undefined
      );
      inbox.removeMessages([uid]);
      if (seenBefore === false) adjustFolderUnread(fold, -1);
      closeActions();
      onCloseReader?.();

      const res = await deleteMailMessage({
        accountId,
        folder: fold,
        uid,
        trashFolder: resolveTrashFolder?.(accountId) ?? undefined,
      });
      if (!res.ok) {
        onRecover?.();
        return;
      }

      const folders = res.data.trashFolder ? [fold, res.data.trashFolder] : [fold];
      refresh.invalidateFolders({
        accountId,
        folders,
        prefetchFolders: res.data.trashFolder ? [res.data.trashFolder] : [],
        prefetchRefresh: true,
        reloadListIfViewing: false,
        forceList: false,
      });
    },
    [
      accountId,
      adjustFolderUnread,
      closeActions,
      folder,
      inbox,
      inboxListActive,
      isOutboxView,
      mailSnooze,
      messageImapFolder,
      onCloseReader,
      onRecover,
      refresh,
      resolveTrashFolder,
    ]
  );

  const runAction = useCallback(
    async (actionId: string, uids: number[]) => {
      if (!account || isOutboxView || uids.length === 0) return;

      if (actionId === "archiveToCoffre") {
        const msg = actionMessage ?? findMessageByUid(uids[0]);
        if (msg) onArchiveToCoffre?.(msg);
        return;
      }

      if (actionId === "delete") {
        await deleteMessage(uids[0], findMessageByUid(uids[0])?.seen);
        return;
      }

      if (actionId === "move") {
        pendingMoveUidsRef.current = uids;
        setMoveDialogOpen(true);
        return;
      }

      if (actionId === "snooze:pick" && uids.length === 1 && !isSnoozedView) {
        if (snoozeBusyRef.current) {
          window.alert("Mise en attente en cours — patientez un instant.");
          return;
        }
        const gmailLocal = isGmailMailAccount(account);
        const holdFolder = resolveSnoozeHoldFolder?.() ?? null;
        if (!gmailLocal && !holdFolder) {
          window.alert(
            "Branchez un dossier IMAP « En attente » (Paramètres → Organisation des dossiers), ou créez-en un."
          );
          onSnoozeFolderRequired?.();
          return;
        }
        const msg = findMessageByUid(uids[0]);
        setSnoozeDialog({ uid: uids[0], subject: msg?.subject });
        return;
      }

      if (actionId === "snooze:cancel" && uids.length === 1) {
        const uid = uids[0];
        const entry = mailSnooze.snoozes.find((s) => s.uid === uid);
        void mailSnooze.unsnooze(entry?.folder ?? folder, uid);
        onCloseReader?.();
        return;
      }

      if (actionId === "markRead") {
        await applySeen(uids[0], true);
        return;
      }

      if (actionId === "markUnread") {
        await applySeen(uids[0], false);
        return;
      }

      if (actionId === "toggleImportant") {
        const msg = findMessageByUid(uids[0]);
        inbox.markMessageFlagged(uids[0], !msg?.flagged);
        return;
      }

      if (actionId === "toggleFavorite") {
        const msg = findMessageByUid(uids[0]);
        const label = messageLabels.labels.get(uids[0]);
        const favorite = !(label?.favorite ?? msg?.favorite);
        void messageLabels.updateLabel(uids[0], { favorite });
        return;
      }

      if (actionId.startsWith("color:")) {
        const color: MailColorLabel | null =
          actionId === "color:none" ? null : (actionId.slice(6) as MailColorLabel);
        void messageLabels.updateLabel(uids[0], { color });
        return;
      }

      if (uids.length !== 1) return;
      const uid = uids[0];

      if (actionId === "reply") {
        await compose.openComposeForUid(uid, "reply");
        return;
      }
      if (actionId === "replyAll") {
        await compose.openComposeForUid(uid, "replyAll");
        return;
      }
      if (actionId === "forward") {
        await compose.openComposeForUid(uid, "forward");
      }
    },
    [
      account,
      actionMessage,
      applySeen,
      compose,
      deleteMessage,
      findMessageByUid,
      folder,
      inbox,
      isOutboxView,
      isSnoozedView,
      mailSnooze,
      messageLabels,
      onArchiveToCoffre,
      onCloseReader,
    ]
  );

  const handleSheetAction = useCallback(
    async (actionId: string) => {
      const msg = actionMessage;
      if (!msg) return;
      await runAction(actionId, [msg.uid]);
      if (actionId !== "move" && actionId !== "snooze:pick") {
        closeActions();
      }
    },
    [actionMessage, closeActions, runAction]
  );

  const handleReaderAction = useCallback(
    async (uid: number, actionId: string) => {
      await runAction(actionId, [uid]);
    },
    [runAction]
  );

  const confirmMove = useCallback(
    async (targetFolder: string) => {
      if (!account || isOutboxView) return;
      const uids = pendingMoveUidsRef.current;
      if (uids.length === 0) return;

      setMoveDialogOpen(false);
      pendingMoveUidsRef.current = [];

      for (const uid of uids) {
        const msg = findMessageByUid(uid);
        if (msg && !msg.seen) {
          adjustFolderUnread(messageImapFolder(uid), -1);
        }
      }
      if (inboxListActive) inbox.removeMessages(uids);
      onCloseReader?.();
      closeActions();

      const bySourceFolder = new Map<string, number[]>();
      for (const uid of uids) {
        const fold = messageImapFolder(uid);
        const list = bySourceFolder.get(fold) ?? [];
        list.push(uid);
        bySourceFolder.set(fold, list);
      }

      for (const [sourceFolder, folderUids] of bySourceFolder) {
        holdMutationUids(account.id, sourceFolder, folderUids);
      }

      const releaseAllMoveHolds = () => {
        for (const [sourceFolder, folderUids] of bySourceFolder) {
          releaseMutationUids(account.id, sourceFolder, folderUids);
        }
      };

      void (async () => {
        try {
          for (const [sourceFolder, folderUids] of bySourceFolder) {
            const res = await moveMailMessage({
              accountId: account.id,
              folder: sourceFolder,
              uids: folderUids,
              targetFolder,
            });

            if (!res.ok) {
              releaseAllMoveHolds();
              window.alert(res.message);
              refresh.recoverAfterMutationError();
              return;
            }
            releaseMutationHoldsOnly(account.id, sourceFolder, folderUids);
            await relocateStoredMessages(account.id, folderUids, sourceFolder, targetFolder);
          }

          const sourceFolders = [...bySourceFolder.keys()];
          const viewingSource = inboxListActive && sourceFolders.includes(folder);

          refresh.invalidateFolders({
            accountId: account.id,
            folders: [...sourceFolders, targetFolder],
            preserveSnapshots: viewingSource ? [folder] : [],
            prefetchFolders: [targetFolder],
            prefetchRefresh: true,
            reloadListIfViewing: inboxListActive && folder === targetFolder,
            forceList: false,
          });
        } catch (err) {
          releaseAllMoveHolds();
          window.alert(err instanceof Error ? err.message : "Déplacement impossible.");
          refresh.recoverAfterMutationError();
        }
      })();
    },
    [
      account,
      adjustFolderUnread,
      closeActions,
      findMessageByUid,
      folder,
      inbox,
      inboxListActive,
      isOutboxView,
      messageImapFolder,
      onCloseReader,
      refresh,
    ]
  );

  const confirmSnooze = useCallback(
    (untilIso: string) => {
      const pending = snoozeDialog;
      if (!pending || isSnoozedView || snoozeBusyRef.current) return;

      const gmailLocal = isGmailMailAccount(account);
      const holdFolder = resolveSnoozeHoldFolder?.() ?? null;
      if (!gmailLocal && !holdFolder) {
        setSnoozeDialog(null);
        window.alert(
          "Branchez un dossier IMAP « En attente » (Paramètres → Organisation des dossiers), ou créez-en un."
        );
        onSnoozeFolderRequired?.();
        return;
      }

      const imapFolder = messageImapFolder(pending.uid);
      const uid = pending.uid;
      const subject = pending.subject ?? undefined;

      if (inboxListActive && !gmailLocal) inbox.removeMessages([uid]);
      if (!gmailLocal && holdFolder && accountId != null) {
        relocateMessageBodyCache(accountId, uid, imapFolder, holdFolder);
      }

      setSnoozeDialog(null);
      onCloseReader?.();
      closeActions();

      if (accountId != null && !gmailLocal) {
        holdMutationUids(accountId, imapFolder, [uid]);
      }

      const releaseSnoozeSourceHold = () => {
        if (accountId == null || gmailLocal) return;
        releaseMutationUids(accountId, imapFolder, [uid]);
      };

      snoozeBusyRef.current = true;
      setSnoozeBusy(true);

      const runSnooze = async () => {
        try {
          const snoozeResult = await mailSnooze.snooze(imapFolder, uid, untilIso, subject, {
            holdFolder: gmailLocal ? null : holdFolder,
          });
          if (!snoozeResult.ok) {
            releaseSnoozeSourceHold();
            window.alert(
              snoozeResult.message?.trim() || "Impossible d'enregistrer le report."
            );
            void mailSnooze.reload({ silent: true });
            refresh.recoverAfterMutationError();
            return;
          }
          if (accountId != null && !gmailLocal) {
            releaseMutationHoldsOnly(accountId, imapFolder, [uid]);
          }
          if (accountId != null) {
            if (!gmailLocal && holdFolder) {
              void relocateStoredMessages(accountId, [uid], imapFolder, holdFolder);
              refresh.invalidateFolders({
                accountId,
                folders: [imapFolder, holdFolder],
                preserveSnapshots: inboxListActive && folder === imapFolder ? [imapFolder] : [],
                prefetchFolders: [holdFolder],
                reloadListIfViewing: folder === holdFolder,
                forceList: false,
              });
            } else {
              refresh.invalidateFolders({
                accountId,
                folders: [imapFolder],
                preserveSnapshots: inboxListActive && folder === imapFolder ? [imapFolder] : [],
                forceList: false,
              });
            }
          }
        } catch {
          releaseSnoozeSourceHold();
          window.alert("Impossible d'enregistrer le report.");
          refresh.recoverAfterMutationError();
        } finally {
          snoozeBusyRef.current = false;
          setSnoozeBusy(false);
        }
      };

      if (gmailLocal) {
        void runSnooze();
      } else {
        void enqueueMailMutation(runSnooze);
      }
    },
    [
      accountId,
      closeActions,
      folder,
      inbox,
      inboxListActive,
      isSnoozedView,
      mailSnooze,
      messageImapFolder,
      onCloseReader,
      onSnoozeFolderRequired,
      refresh,
      resolveSnoozeHoldFolder,
      snoozeDialog,
    ]
  );

  const sheetItems = useMemo(() => {
    if (!actionMessage) return [];
    const label = messageLabels.labels.get(actionMessage.uid);
    return buildMessageContextMenuItems({
      compose: true,
      seen: actionMessage.seen,
      flagged: actionMessage.flagged,
      favorite: label?.favorite ?? actionMessage.favorite,
      colorLabel: label?.color ?? actionMessage.colorLabel,
      snoozed:
        isSnoozedView || mailSnooze.isSnoozed(messageImapFolder(actionMessage.uid), actionMessage.uid),
      showSnooze: !isDraftsFolder && !isOutboxView,
      showArchiveToCoffre: !isOutboxView,
    });
  }, [
    actionMessage,
    isDraftsFolder,
    isOutboxView,
    isSnoozedView,
    mailSnooze,
    messageImapFolder,
    messageLabels.labels,
  ]);

  return {
    actionMessage,
    openActions,
    closeActions,
    handleSheetAction,
    handleReaderAction,
    sheetItems,
    markUnread: (uid: number, seenBefore?: boolean) => {
      void applySeen(uid, false);
      void seenBefore;
    },
    deleteMessage,
    moveDialogOpen,
    openMoveDialog: (uids: number[]) => {
      pendingMoveUidsRef.current = uids;
      setMoveDialogOpen(true);
    },
    closeMoveDialog: () => {
      setMoveDialogOpen(false);
      pendingMoveUidsRef.current = [];
    },
    confirmMove,
    snoozeDialog,
    snoozeBusy,
    closeSnoozeDialog: () => setSnoozeDialog(null),
    confirmSnooze,
    openDraftForEdit: compose.openDraftForEdit,
  };
}
